"use client";

import { observer } from "mobx-react-lite";
import { type FormEvent, useEffect, useState } from "react";

import {
  X,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useAppContext } from "../../../shared/functions/Context";
import { defaultMeasure, IMeasure } from "../../../shared/models/Measure";
import useIndividualScorecard from "../../../shared/hooks/useIndividualScorecard";
import { hideModalFromId } from "../../../shared/functions/ModalShow";
import MODAL_NAMES from "../ModalName";
import {
  MAIL_EMAIL,
  MAIL_MIDTERM_RATINGS_DISPUTE_ME,
} from "../../../shared/functions/mailMessages";
import { IScorecardMetadata } from "../../../shared/models/ScorecardMetadata";

const SubmitMidtermDisputeResolutionModal = observer(() => {
  const { api, store, ui } = useAppContext();

  const [measure, setMeasure] = useState<IMeasure>({ ...defaultMeasure });
  const [employeeDisputeResolution, setEmployeeDisputeResolution] =
    useState("");
  const [supervisorDisputeResolution, setSupervisorDisputeResolution] =
    useState("");
  const [disputeType, setDisputeType] = useState("");
  const [newRating, setNewRating] = useState<number>();
  const [preferredRating, setPreferredRating] = useState<number>();
  const [activeTab, setActiveTab] = useState<"subordinate" | "supervisor">(
    "subordinate"
  );
  const [loading, setLoading] = useState<boolean>(false);

  const maxCharCount = 2000;
  const uid = store.auth.meJson?.uid;
  const agreement = useIndividualScorecard(uid);
  const measures = store.measure.getByUid(uid!);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (activeTab === "subordinate") {
      setActiveTab("supervisor");
    } else {
      const selected = store.measure.selected;
      if (!selected) return;

      setLoading(true);
      await updateDispute(
        measure,
        employeeDisputeResolution,
        preferredRating,
        disputeType,
        supervisorDisputeResolution
      );
      setLoading(false);
      onSubmit();
      onCancel();
    }
  };

  const updateDispute = async (
    measure: IMeasure,
    reason: string,
    rating?: number,
    disputeType?: string,
    additionalComments?: string
  ) => {
    try {
      await api.measure.update(
        {
          ...measure,
          disputeReason: reason,
          preferredRating: rating,
          disputeType,
          additionalComments,
          employeeDisputeResolution,
          supervisorDisputeResolution,
          newRating,
          finalRating: newRating ?? null,
          // finalRating2: newRating ?? null,
          isResolved: true,
        },
        [
          "disputeReason",
          "preferredRating",
          "disputeType",
          "additionalComments",
          "employeeDisputeResolution",
          "supervisorDisputeResolution",
          "newRating",
          "finalRating",
          "finalRating2",
          "isResolved",
        ]
      );
    } catch (error) {
      console.log(error);
    }
  };

  const onCancel = () => {
    store.measure.clearSelected();
    setMeasure({ ...defaultMeasure });
    setEmployeeDisputeResolution("");
    setPreferredRating(undefined);
    setDisputeType("");
    setSupervisorDisputeResolution("");
    setNewRating(undefined);
    setActiveTab("subordinate");
    hideModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_RESOLVE_DISPUTE_Q2_MODAL);
  };

  const incompleteReviewError = measures.some(
    (m) => m.asJson.supervisorRating === null
  );

  const onSubmit = async () => {
    const selectedUser = store.auth.meJson;
    if (incompleteReviewError) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Incomplete review. Some measures are not rated.",
        timeoutInMs: 10000,
      });
      return;
    } else if (!selectedUser) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "User not found, email not sent.",
        timeoutInMs: 10000,
      });
      return;
    }

    const { MY_SUBJECT, MY_BODY } = MAIL_MIDTERM_RATINGS_DISPUTE_ME(
      selectedUser.displayName,
      selectedUser.displayName
    );

    const $agreement: IScorecardMetadata = {
      ...agreement,
      midtermRated: true,
      isDispute: true,
    };
    await api.individualScorecard.update($agreement);
    await api.mail.sendMailCC(
      ["systems@lotsinsights.com", selectedUser.email!],
      [selectedUser.email!],
      MAIL_EMAIL,
      MY_SUBJECT,
      MY_BODY
    );
    ui.snackbar.load({
      id: Date.now(),
      type: "success",
      message: "Dispute resolved and email sent successfully.",
      timeoutInMs: 10000,
    });
  };

  useEffect(() => {
    const selectedMeasure = store.measure.selected;
    if (!selectedMeasure) {
      setMeasure({ ...defaultMeasure });
      setEmployeeDisputeResolution("");
      setPreferredRating(undefined);
      setDisputeType("");
      setSupervisorDisputeResolution("");
      setNewRating(undefined);
    } else {
      setMeasure({ ...defaultMeasure, ...selectedMeasure });
      setEmployeeDisputeResolution(
        selectedMeasure.employeeDisputeResolution || ""
      );
      setPreferredRating(selectedMeasure.preferredRating || undefined);
      setDisputeType(selectedMeasure.disputeType || "");
      setSupervisorDisputeResolution(
        selectedMeasure.supervisorDisputeResolution || ""
      );
      setNewRating(selectedMeasure.newRating || undefined);
    }
  }, [store.measure.selected]);

  useEffect(() => {
    const uid = store.auth.meJson?.uid;
    const load = async () => {
      if (!uid) return;
      setLoading(true);
      await api.objective.getAll(uid);
      await api.measure.getAll(uid);
      setLoading(false);
    };
    load();
  }, [api.measure, api.objective]);

  const disputeExists =
    employeeDisputeResolution ||
    preferredRating ||
    disputeType ||
    supervisorDisputeResolution;

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-auto p-8">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-[#faa05a]" />
            {disputeExists ? "Edit Dispute Resolution" : "Resolve Dispute"}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Review and resolve the scorecard dispute
          </p>
        </div>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          type="button"
          onClick={() => setActiveTab("subordinate")}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            activeTab === "subordinate"
              ? "bg-white text-[#3f6a8e] shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Employee Information
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("supervisor")}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            activeTab === "supervisor"
              ? "bg-white text-[#3f6a8e] shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Supervisor Resolution
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === "subordinate" && (
          <div className="space-y-6">
            <div>
              <label
                htmlFor="sub-resolution"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Employee Dispute Resolution{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent resize-none"
                id="sub-resolution"
                rows={5}
                placeholder="Enter your dispute resolution comments..."
                value={employeeDisputeResolution}
                onChange={(e) => setEmployeeDisputeResolution(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {employeeDisputeResolution.length}/{maxCharCount} characters
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="disputed-rating"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Disputed Rating
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent"
                  id="disputed-rating"
                  value={preferredRating ?? ""}
                  onChange={(e) => setPreferredRating(Number(e.target.value))}
                  min="1"
                  max="5"
                  step="0.1"
                  readOnly
                />
              </div>
              <div>
                <label
                  htmlFor="new-rating"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  New Rating <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent"
                  id="new-rating"
                  placeholder="Enter new rating"
                  value={newRating ?? ""}
                  onChange={(e) => setNewRating(Number(e.target.value))}
                  min="1"
                  max="5"
                  step="0.1"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !employeeDisputeResolution.trim()}
                className="px-6 py-3 bg-[#84cbe9] text-white rounded-lg hover:bg-[#3f6a8e] transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next Step
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {activeTab === "supervisor" && (
          <div className="space-y-6">
            <div>
              <label
                htmlFor="super-resolution"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Supervisor Resolution Comments{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent resize-none"
                id="super-resolution"
                rows={5}
                placeholder="Provide your resolution comments and justification for the new rating..."
                value={supervisorDisputeResolution}
                onChange={(e) => setSupervisorDisputeResolution(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {supervisorDisputeResolution.length}/{maxCharCount} characters
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#84cbe9]" />
                Rating Summary
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Disputed Rating:</span>
                  <span className="ml-2 font-semibold text-gray-900">
                    {preferredRating || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">New Rating:</span>
                  <span className="ml-2 font-semibold text-[#84cbe9]">
                    {newRating || "Not set"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between gap-3 pt-4">
              <button
                type="button"
                onClick={() => setActiveTab("subordinate")}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={loading || !supervisorDisputeResolution.trim()}
                className="px-6 py-3 bg-[#4bb543] text-white rounded-lg hover:bg-green-600 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Submit Resolution
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
});

export default SubmitMidtermDisputeResolutionModal;
